import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import LaunchIcon from "@mui/icons-material/Launch";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SyncIcon from "@mui/icons-material/Sync";
import { Autocomplete, CircularProgress, IconButton, Stack, TextField, Typography } from "@mui/material";
import { DealCreate } from "components/modals";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { Dayjs } from "dayjs";
import { RequestQueue, useActionHook, useCanCreateDeal, useFormatDateHook, useModals, useNavigate, usePrompt } from "hooks";
import {
  useConnectionSelect,
  useDataRoomsActions,
  useDateFormatSelect,
  useReportsActions,
  useReportsMetaSelector
} from "store";
import { DealBase, IConnectionProcess, IFolder } from "store/types";

import { CLOUD_MIRROR_SECTION_NAME } from "../../constants/datarooms";

interface Props {
  dataRoomUrlId?: number;
}

const dealToAutocomplete = (deal: DealBase): { id: number; label: string } => ({
  id: deal?.id,
  label: deal?.title
});

// Simple module-level storage for scroll position
let savedScrollPosition: number | null = null;

export const DataroomFetchers: React.FC<Props> = memo(({ dataRoomUrlId }: Props) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const connection = useConnectionSelect();
  const { deals } = useReportsMetaSelector();
  const formatDateHook = useFormatDateHook();
  const dateFormat = useDateFormatSelect();
  const formatDate = (date?: string | number | Date | Dayjs | null | undefined) => {
    return formatDateHook({ date, format: dateFormat });
  };
  const navigate = useNavigate();
  const openPrompt = usePrompt();
  const canCreateDeal = useCanCreateDeal();

  const handleOpenDealPrompt = useCallback(
    async (dealName: string) => {
      const result = await openPrompt({
        Component: DealCreate,
        props: {
          status_id: 4,
          dealName
        }
      });
      return result;
    },
    [openPrompt]
  );

  const { getDealDataroomRoot } = useDataRoomsActions();

  const getCloudMirrorFolder = async (dealId: number): Promise<IFolder | undefined> => {
    try {
      const folders = await getDealDataroomRoot({ dealId });
      const folderArray = Object.values(folders);
      return folderArray.find((folder) => folder.title === CLOUD_MIRROR_SECTION_NAME);
    } catch (_error) {
      return undefined;
    }
  };

  const [relateionLoadings, setRelationLoadings] = useState<(number | string)[]>([]);

  const { updateRelationAction, deleteRelationAction, syncFetcherAction, resetFiles } = useDataRoomsActions();
  const { getMetaAction } = useReportsActions();
  const { successModal } = useModals();

  const dealsAutocompleteOptions: { id: number; label: string }[] = useMemo(
    () => deals.map(dealToAutocomplete),
    [deals]
  );

  const { action: updateRelation } = useActionHook(updateRelationAction, {
    onSuccess: (res: number | string) => {
      setTimeout(() => {
        setRelationLoadings((prev) => {
          const filteredRelation = prev.filter((item) => item !== res);
          return filteredRelation;
        });
        if (savedScrollPosition !== null && scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = savedScrollPosition;
          savedScrollPosition = null;
        }
      }, 0);
    }
  });

  const { action: deleteRelation } = useActionHook(deleteRelationAction, {
    onSuccess: (res: number | string) => {
      setTimeout(() => {
        setRelationLoadings((prev) => {
          const filteredRelation = prev.filter((item) => item !== res);
          return filteredRelation;
        });
        if (savedScrollPosition !== null && scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = savedScrollPosition;
          savedScrollPosition = null;
        }
      }, 0);
    }
  });

  const { action: syncFetcher } = useActionHook(syncFetcherAction, {
    onSuccess: (msg: string) => successModal({ msg })
  });

  const { action: resetFilesAction } = useActionHook(resetFiles, {
    onSuccess: (msg: string) => successModal({ msg })
  });

  const requestQueue = new RequestQueue();

  // Simple: restore scroll when relations change
  useEffect(() => {
    if (savedScrollPosition !== null && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = savedScrollPosition;
    }
  }, [connection?.relations]);

  return (
    <Stack
      sx={{
        alignItems: "stretch",
        flexGrow: 0,
        backgroundColor: "white",
        borderRadius: "12px",
        overflow: "hidden",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
        border: "1px solid #e8f0fe"
      }}
    >
      <Stack
        direction="row"
        sx={{
          flexShrink: 0,
          alignItems: "center",
          padding: "0 30px",
          height: "72px",
          backgroundColor: "#f8fafc",
          borderBottom: "2px solid #e2e8f0",
          fontSize: "16px"
        }}
      >
        <Stack direction="row" sx={{ width: "20%", alignItems: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#597380", fontSize: "16px" }}>
            Data room Deals
          </Typography>
        </Stack>
        <Stack direction="row" sx={{ flexGrow: 1, alignItems: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#597380", fontSize: "16px" }}>
            Altius Deal
          </Typography>
        </Stack>
      </Stack>
      <Stack
        ref={scrollContainerRef}
        sx={{
          overflow: "auto"
        }}
      >
        {connection?.relations?.map((item) => (
          <Stack
            direction="row"
            sx={{
              flexShrink: 0,
              alignItems: "center",
              padding: "16px 30px",
              minHeight: "72px",
              borderBottom: "solid 1px #dae0e2",
              fontSize: "16px",
              backgroundColor: "white"
            }}
            key={item.supplier.id}
          >
            <Stack direction="row" sx={{ width: "20%", alignItems: "center" }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 400,
                  color: "#1e293b",
                  fontSize: "15px",
                  lineHeight: 1.4
                }}
              >
                {item.supplier.name}
              </Typography>
            </Stack>
            <Stack direction="row" sx={{ flexGrow: 1, gap: "12px", alignItems: "center" }}>
              <Autocomplete
                disablePortal
                disableListWrap
                options={dealsAutocompleteOptions}
                sx={{
                  ".MuiFormControl-root": {
                    marginBottom: 0
                  },
                  ".MuiSvgIcon-root": {
                    color: "#64748b"
                  },
                  ".MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "#f8fafc",
                    "&:hover": {
                      backgroundColor: "#f1f5f9"
                    },
                    "&.Mui-focused": {
                      backgroundColor: "white",
                      boxShadow: "0 0 0 2px #3b82f6"
                    }
                  },
                  "&.MuiAutocomplete-root.MuiAutocomplete-root [class*='Indicator']": {
                    height: "32px !important",
                    width: "32px !important",
                    padding: "6px !important",
                    margin: "0 !important",
                    maxHeight: "32px !important"
                  }
                }}
                slotProps={{
                  clearIndicator: {
                    sx: {
                      height: "32px !important",
                      width: "32px !important",
                      padding: "6px !important",
                      margin: "0 !important",
                      maxHeight: "32px !important"
                    }
                  },
                  popupIndicator: {
                    sx: {
                      height: "32px !important",
                      width: "32px !important",
                      padding: "6px !important",
                      margin: "0 !important",
                      maxHeight: "32px !important"
                    }
                  }
                }}
                isOptionEqualToValue={(op, val) => val?.id === op.id}
                value={
                  item.consumer
                    ? {
                        id: item.consumer.dealId,
                        label: dealsAutocompleteOptions.find((d) => d.id === item.consumer?.dealId)?.label || ""
                      }
                    : null
                }
                onChange={(_, option) => {
                  // Save scroll position before update/delete
                  savedScrollPosition = scrollContainerRef.current?.scrollTop || null;

                  if (option) {
                    if (dealsAutocompleteOptions.find((d) => d.id === item.consumer?.dealId)?.label === option.label)
                      return;
                    requestQueue.addRequest({
                      func: updateRelation,
                      args: [
                        {
                          supplierId: item.supplier.id,
                          dataroomId: connection?.id,
                          consumerId: option?.id
                        }
                      ]
                    });
                  } else {
                    item.consumer &&
                      deleteRelation({
                        supplierId: item.supplier.id,
                        dataroomId: connection?.id,
                        consumerId: item.consumer?.id
                      });
                  }

                  setRelationLoadings([...relateionLoadings, item.supplier.id]);
                }}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    key={option.id}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      if (props.onClick) {
                        props.onClick(e);
                      }
                    }}
                  >
                    {option.label}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    sx={{
                      ".MuiOutlinedInput-root": {
                        padding: "8px 12px !important",
                        width: "420px",
                        fontSize: "14px"
                      },
                      ".MuiInputBase-input": {
                        fontSize: "14px",
                        color: "#1e293b"
                      }
                    }}
                    placeholder="Select or create a deal..."
                    variant="outlined"
                    size="small"
                  />
                )}
              />
              {relateionLoadings.includes(item.supplier.id) && (
                <MaterialTooltip isShow title="Loading...">
                  <CircularProgress size={20} />
                </MaterialTooltip>
              )}
              {item.consumer && (
                <>
                  <MaterialTooltip isShow title="Open Altius deal">
                    <IconButton
                      onClick={async () => {
                        if (!item.consumer?.dealId) return;
                        const cloudMirrorFolder = await getCloudMirrorFolder(item.consumer.dealId);
                        if (cloudMirrorFolder) {
                          navigate({
                            route: "dealDataRoom",
                            params: { deal_id: item.consumer.dealId, folder_id: cloudMirrorFolder.id },
                            search: { section: cloudMirrorFolder.id }
                          });
                        } else {
                          navigate({ route: "deal", params: { deal_id: item.consumer.dealId } });
                        }
                      }}
                      sx={iconBtnsStyles}
                      size="sm"
                    >
                      <LaunchIcon sx={{ fontSize: "20px" }} />
                    </IconButton>
                  </MaterialTooltip>
                  <MaterialTooltip isShow title="Index files & Sync">
                    <IconButton
                      onClick={() => {
                        if (!dataRoomUrlId || !item?.consumer?.id) return;
                        syncFetcher(dataRoomUrlId, item.consumer.id);
                      }}
                      sx={iconBtnsStyles}
                      size="sm"
                    >
                      <SyncIcon sx={{ fontSize: "20px" }} />
                    </IconButton>
                  </MaterialTooltip>
                  <MaterialTooltip isShow title="Reset last index date">
                    <IconButton
                      onClick={() => {
                        if (!dataRoomUrlId || !item?.consumer?.id) return;
                        resetFilesAction(dataRoomUrlId, item.consumer.id);
                      }}
                      sx={iconBtnsStyles}
                      size="sm"
                    >
                      <RestartAltIcon sx={{ fontSize: "20px" }} />
                    </IconButton>
                  </MaterialTooltip>
                </>
              )}
              {canCreateDeal && item.consumer == null && !relateionLoadings.includes(item.supplier.id) && (
                <MaterialTooltip isShow title="Create new deal">
                  <IconButton
                    onMouseDown={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      savedScrollPosition = scrollContainerRef.current?.scrollTop || 0;
                      const dealBase = await handleOpenDealPrompt(item.supplier.name);
                      if (!dealBase) {
                        savedScrollPosition = null;
                        return;
                      }
                      getMetaAction();
                      setRelationLoadings([...relateionLoadings, item.supplier.id]);
                      requestQueue.addRequest({
                        func: updateRelation,
                        args: [
                          {
                            supplierId: item.supplier.id,
                            dataroomId: connection?.id,
                            consumerId: dealBase.id,
                            new_deal: true
                          }
                        ]
                      });
                    }}
                    sx={iconBtnsStyles}
                    size="sm"
                  >
                    <NoteAddOutlinedIcon sx={{ fontSize: "20px" }} />
                  </IconButton>
                </MaterialTooltip>
              )}
            </Stack>
            {item.consumer && (
              <Stack
                sx={{
                  width: "300px",
                  alignItems: "flex-end"
                }}
              >
                <Stack
                  direction="column"
                  sx={{
                    alignItems: "flex-end"
                  }}
                >
                  <Stack direction="row" gap="12px" sx={{ alignItems: "center" }}>
                    {item.last_sync && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#64748b",
                          fontSize: "12px",
                          fontWeight: 500
                        }}
                      >
                        Last sync: {formatDate(item.last_sync)}
                      </Typography>
                    )}
                    {item.last_index && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#64748b",
                          fontSize: "12px",
                          fontWeight: 500
                        }}
                      >
                        Last index: {formatDate(item.last_index)}
                      </Typography>
                    )}
                  </Stack>
                  <Stack direction="row" gap="12px" sx={{ alignItems: "center" }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#64748b",
                        fontSize: "12px",
                        fontWeight: 500
                      }}
                    >
                      Indexed: {item.files?.indexed || "0"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#64748b",
                        fontSize: "12px",
                        fontWeight: 500
                      }}
                    >
                      Synced: {item.files?.downloaded || "0"}
                    </Typography>
                    <Stack direction="row" gap="4px" alignItems="center">
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#64748b",
                          fontSize: "12px",
                          fontWeight: 500
                        }}
                      >
                        Processes:
                      </Typography>
                      <MaterialTooltip
                        title={
                          <Stack gap="15px">
                            {item.processes &&
                              item.processes.length &&
                              item.processes.map((process: IConnectionProcess, index: number) => (
                                <Stack key={index}>
                                  <Typography>Id: {process.id}</Typography>
                                  <Typography>Full name: {process.full_name}</Typography>
                                  <Typography>Operation: {process.operation}</Typography>
                                  <Typography>Status: {process.status}</Typography>
                                  <Typography>Created at: {formatDate(process.created_at)}</Typography>
                                  <Typography>Updated at: {formatDate(process.updated_at)}</Typography>
                                </Stack>
                              ))}
                          </Stack>
                        }
                        isShow={Boolean(item.processes && item.processes.length)}
                      >
                        <Typography
                          sx={{
                            color: item.processes && item.processes.length ? "#26A6AE" : "#94a3b8",
                            cursor: item.processes && item.processes.length ? "help" : "default",
                            fontSize: "12px",
                            fontWeight: 500
                          }}
                        >
                          {item.processes && item.processes.length ? item.processes.length : "0"}
                        </Typography>
                      </MaterialTooltip>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            )}
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
});

const iconBtnsStyles = {
  color: "#64748b",
  background: "white",
  border: "1px solid #e2e8f0",
  borderRadius: "8px",
  minWidth: "40px",
  minHeight: "40px",
  padding: "8px",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: "#f8fafc",
    borderColor: "#3b82f6",
    color: "#3b82f6",
    transform: "translateY(-1px)",
    boxShadow: "0 2px 8px rgba(59, 130, 246, 0.15)"
  }
};
